import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';

import type { DoorVisitReportApi, DoorVisitReportRun } from './doorVisitReportApi';
import { DoorVisitReportStatus } from '../graphql/__generated__/graphql';

const activateDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
};

export type DoorVisitReportDownloadButtonProps = {
  report: DoorVisitReportRun;
  api: Pick<DoorVisitReportApi, 'getReportDownload'>;
  accessibleName?: string;
  showGeneratingState?: boolean;
  compact?: boolean;
};

export const DoorVisitReportDownloadButton = ({
  report,
  api,
  accessibleName = 'Download report',
  showGeneratingState = false,
  compact = false,
}: DoorVisitReportDownloadButtonProps) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCompleted = report.status === DoorVisitReportStatus.Completed;
  const isGenerating = report.status === DoorVisitReportStatus.Queued || report.status === DoorVisitReportStatus.Running;

  if (!isCompleted && (!showGeneratingState || !isGenerating)) return null;

  const handleDownload = async (): Promise<void> => {
    if (downloading) return;
    setDownloading(true);
    setError(null);

    try {
      const download = await api.getReportDownload(report.id);
      activateDownload(download.url, download.fileName);
    } catch {
      setError('Could not prepare the report download. Try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {compact ? (
        <Tooltip title={accessibleName}>
          <span>
            <IconButton
              size="small"
              onClick={isCompleted ? () => void handleDownload() : undefined}
              disabled={isGenerating || downloading}
              aria-busy={isGenerating || downloading}
              aria-label={accessibleName}
            >
              {downloading ? <CircularProgress size={16} aria-hidden /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          size="small"
          variant="outlined"
          onClick={isCompleted ? () => void handleDownload() : undefined}
          disabled={isGenerating || downloading}
          aria-busy={isGenerating || downloading}
          aria-label={accessibleName}
        >
          {isGenerating || downloading ? <CircularProgress size={16} aria-hidden /> : 'Download'}
          {isGenerating && 'Generating'}
        </Button>
      )}
      {error !== null && <span role="alert">{error}</span>}
    </>
  );
};
